import { EventContentArg } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { createPortal } from "react-dom";
import { useRef, useState } from "react";
import {
  FunctionField,
  RecordContextProvider,
  ReferenceOneField,
  TextField,
  useGetManyReference,
  useRecordContext,
} from "react-admin";
import { grants } from "../../backoffice.access_control";
import { LinkToFirestoreField } from "../../fields/LinkToFirestoreField";
import { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import { Delayed } from "../../misc/Delayed";
import { Instructor } from "../../providers/instructorsProvider";
import { autovioColors, overviewCardStyle } from "../../misc/backofficeTheme";
import { DateRange } from "../../model/DateRange";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { useEventDialog } from "../../hooks/useEventDialog";
import { CalendarEventStatusField } from "../../fields/CalendarEventStatusField";
import { autovioCalendarEventToEventInput, wasCancelled } from "../../utils/calendar";
import { useFullcalendarCustomButtonAnchor } from "../../hooks/useFullcalendarCustomButtonAnchor";
import { useMenu } from "../../hooks/useMenu";

type AppointmentsCalendarView = "listWeek" | "listMonth";

const calendarViewChoices = [
  { id: "listWeek", name: "Woche" },
  { id: "listMonth", name: "Monat" },
] as const satisfies Array<{ id: AppointmentsCalendarView; name: string }>;

export function InstructorAppointmentsList() {
  const calendarRef = useRef<FullCalendar>(null);
  const [dateRange, setDateRange] = useState<DateRange>([
    DateWithoutTime.fromDateTime(DateTime.now().startOf("week")),
    DateWithoutTime.fromDateTime(DateTime.now().endOf("week")),
  ]);
  const [calendarTitle, setCalendarTitle] = useState("");
  const [calendarView, setCalendarView] = useState<AppointmentsCalendarView>("listWeek");
  const [showOnlyCompletedEvents, setShowOnlyCompletedEvents] = useState(false);
  const showOnlyCompletedEventsToggleAnchor = useFullcalendarCustomButtonAnchor("showOnlyCompletedEventsToggleAnchor");
  const { data, isLoading } = useCalendarData(dateRange);
  const { eventDialog, openEventDialog } = useEventDialog();
  const changeCalendarView = (view: AppointmentsCalendarView) => {
    calendarRef.current?.getApi().changeView(view);
  };

  return (
    <Box
      className="instructor-appointments-list"
      data-testid="InstructorAppointmentsList"
      sx={{ ...overviewCardStyle, p: { xs: 2, sm: "25px 30px" }, position: "relative" }}
    >
      <div className="instructor-appointments-list-mobile-header">
        <h2 className="fc-toolbar-title instructor-appointments-list-title">{calendarTitle}</h2>
        <InstructorAppointmentsListHeaderMenuButton
          activeCalendarView={calendarView}
          changeCalendarView={changeCalendarView}
          setShowOnlyCompletedEvents={setShowOnlyCompletedEvents}
          showOnlyCompletedEvents={showOnlyCompletedEvents}
        />
      </div>
      <FullCalendar
        ref={calendarRef}
        timeZone="Europe/Berlin"
        locale={deLocale}
        plugins={[listPlugin]}
        headerToolbar={{
          left: "title",
          center: "",
          right: "prev today next showOnlyCompletedEventsToggleAnchor listWeek,listMonth",
        }}
        customButtons={{
          showOnlyCompletedEventsToggleAnchor: {},
        }}
        views={{
          listWeek: { buttonText: "Woche" },
          listMonth: { buttonText: "Monat" },
        }}
        initialView="listWeek"
        events={
          data.instructorEvents
            ?.filter((event) => {
              if (showOnlyCompletedEvents) {
                return event.endedAt && !wasCancelled(event);
              }
              return true;
            })
            .map((event) => autovioCalendarEventToEventInput(event)) ?? []
        }
        datesSet={(dates) => {
          setCalendarTitle(dates.view.title);
          if (dates.view.type === "listWeek" || dates.view.type === "listMonth") {
            setCalendarView(dates.view.type);
          }
          const start = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.start, { zone: "Europe/Berlin" }));
          const end = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.end, { zone: "Europe/Berlin" })).prevDay;
          if (dateRange[0].toString() !== start.toString() || dateRange[1].toString() !== end.toString()) {
            setDateRange([start, end]);
          }
        }}
        eventContent={(eventInfo) => <InstructorAppointmentsListItem eventInfo={eventInfo} />}
        eventClick={(eventInfo) => {
          const autovioCalendarEvent = eventInfo.event.extendedProps.event as AutovioCalendarEvent;
          openEventDialog(autovioCalendarEvent);
        }}
      />
      {showOnlyCompletedEventsToggleAnchor &&
        (createPortal(
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyCompletedEvents}
                onChange={(event) => setShowOnlyCompletedEvents(event.target.checked)}
              />
            }
            label="Zeige nur stattgefundene Termine"
          />,
          showOnlyCompletedEventsToggleAnchor,
        ) as any)}
      {isLoading && (
        <Delayed ms={100}>
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              top: 0,
              right: 0,
              height: "100%",
              width: "100%",
              backgroundColor: "background.paper",
              zIndex: 50,
              opacity: "50%",
            }}
          >
            <CircularProgress />
          </Box>
        </Delayed>
      )}
      {eventDialog}
    </Box>
  );
}

function InstructorAppointmentsListHeaderMenuButton(props: {
  activeCalendarView: AppointmentsCalendarView;
  changeCalendarView: (view: AppointmentsCalendarView) => void;
  setShowOnlyCompletedEvents: (showOnlyCompletedEvents: boolean) => void;
  showOnlyCompletedEvents: boolean;
}) {
  const { activeCalendarView, changeCalendarView, setShowOnlyCompletedEvents, showOnlyCompletedEvents } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Terminlisten-Menü öffnen">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem onClick={() => setShowOnlyCompletedEvents(!showOnlyCompletedEvents)}>
          <ListItemText>Zeige nur stattgefundene Termine</ListItemText>
          <Switch
            checked={showOnlyCompletedEvents}
            inputProps={{ "aria-label": "Zeige nur stattgefundene Termine" }}
            onChange={(event) => setShowOnlyCompletedEvents(event.target.checked)}
            onClick={(event) => event.stopPropagation()}
          />
        </MenuItem>
        <ListSubheader>Ansicht</ListSubheader>
        {calendarViewChoices.map((choice) => (
          <MenuItem
            key={choice.id}
            selected={activeCalendarView === choice.id}
            onClick={() => {
              closeMenu();
              changeCalendarView(choice.id);
            }}
          >
            {choice.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

function InstructorAppointmentsListItem(props: { eventInfo: EventContentArg }) {
  const { eventInfo } = props;
  const { event } = eventInfo;
  const autovioCalendarEvent = event.extendedProps.event as AutovioCalendarEvent;
  return (
    <RecordContextProvider value={event.extendedProps.event}>
      <Grid
        container
        columns={{ xs: 1, sm: grants.has("viewFirestore") ? 10 : 9 }}
        data-testid="instructor-appointments-list-item"
        rowSpacing={{ xs: 0.5, sm: 0 }}
        style={{ cursor: "pointer" }}
      >
        <Grid item xs={1} sm={2} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Typography variant="body2">{event.title}</Typography>
        </Grid>
        <Grid
          item
          xs={1}
          sm={3}
          sx={{
            display: { xs: autovioCalendarEvent.student ? "block" : "none", sm: "block" },
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {autovioCalendarEvent.student && (
            <ReferenceOneField reference="students" target="id" source="student.uid">
              <TextField source="name" />
            </ReferenceOneField>
          )}
        </Grid>
        <Grid item xs={1} sm={2} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <CalendarEventStatusField />
        </Grid>
        <Grid item xs={1} sm={2} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <FunctionField
            render={({ editCount, editWithReasonCount }: AutovioCalendarEvent) => {
              const s = editCount === 1 ? "1 x geändert" : editCount > 1 ? `${editCount}x geändert` : null;
              if (editWithReasonCount > 0) {
                return <span style={{ color: autovioColors.red }}>{s}</span>;
              } else {
                return s;
              }
            }}
          />
        </Grid>
        {grants.has("viewFirestore") && (
          <Grid item xs={1} sm={1}>
            <Box display="flex" justifyContent="flex-end">
              <LinkToFirestoreField label="" source="id" collection="/calendar_events" />
            </Box>
          </Grid>
        )}
      </Grid>
    </RecordContextProvider>
  );
}

interface CalendarData {
  data: {
    instructorEvents?: AutovioCalendarEvent[];
  };
  isLoading: boolean;
}

function useCalendarData(dateRange: DateRange): CalendarData {
  const instructor = useRecordContext<Instructor>();
  const { id: instructorId, drivingSchoolId } = instructor;

  const { data: instructorEvents, isLoading: isLoadingEvents } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "drivingSchoolId",
      id: drivingSchoolId,
      filter: { dateRange, instructorId },
      pagination: { page: 1, perPage: 9999 },
      meta: { includeDeleted: true },
    },
  );

  const isLoading = isLoadingEvents;
  return { data: { instructorEvents }, isLoading };
}
